import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Text } from '../base/Text';

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type SpinnerColor = 'primary' | 'white' | 'gray';

interface LoadingSpinnerProps {
	size?: SpinnerSize;
	color?: SpinnerColor;
	className?: string;
	label?: string;
	/** When true, the label text is displayed visibly below the spinner instead of being screen-reader-only */
	showLabel?: boolean;
}

const sizeClasses: Record<SpinnerSize, string> = {
	xs: 'h-3 w-3',
	sm: 'h-4 w-4',
	md: 'h-6 w-6',
	lg: 'h-8 w-8',
	xl: 'h-12 w-12',
};

// Color classes will be determined dynamically in the component

export function LoadingSpinner({
	size = 'md',
	color = 'primary',
	className,
	label = 'Loading...',
	showLabel = false,
}: LoadingSpinnerProps) {
	const colors = useThemeColors();

	const getColorClass = () => {
		switch (color) {
			case 'primary':
				return colors.classes.textPrimary;
			case 'white':
				return colors.classes.textWhite;
			case 'gray':
				return colors.classes.textSecondary;
			default:
				return colors.classes.textPrimary;
		}
	};

	return (
		<div
			className={cn(
				'inline-flex items-center',
				showLabel && 'flex-col gap-2',
				className,
			)}
		>
			<svg
				className={cn('animate-spin', sizeClasses[size], getColorClass())}
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				aria-hidden="true"
			>
				<circle
					className="opacity-25"
					cx="12"
					cy="12"
					r="10"
					stroke="currentColor"
					strokeWidth="4"
				/>
				<path
					className="opacity-75"
					fill="currentColor"
					d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
				/>
			</svg>
			{showLabel ? (
				<Text>{label}</Text>
			) : (
				<Text className="sr-only">&nbsp; {label}</Text>
			)}
		</div>
	);
}
